import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../../components/common';
import { useTemplateViewModel, useTemplatePdfViewModel } from '../../viewmodels';
import { FieldMappingItem, FieldMapping } from '../../models';
import { templateService } from '../../services/documentService';
import { PDFFieldMappingEditor, ImageTemplateEditor } from '../../components/templates';

const FIELD_TYPES = [
  { value: 'text', label: '텍스트' },
  { value: 'textarea', label: '여러 줄 텍스트' },
  { value: 'date', label: '날짜' },
  { value: 'number', label: '숫자' },
  { value: 'select', label: '선택' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 2 }}>
    {value === index && children}
  </Box>
);

const TemplateDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const templateId = parseInt(id!, 10);
  const isEdit = window.location.pathname.includes('/edit');

  const { templates, updateTemplate, isUpdating, refetch } = useTemplateViewModel();
  const template = templates.find((t) => t.id === templateId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_shared: false,
  });
  const [fields, setFields] = useState<FieldMappingItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);

  // 템플릿 PDF 로딩 (공통 훅 사용) - WeDMS 상태 폴링 포함
  const { pdfUrl, isLoading: loadingPdf, error: pdfError, hasPdf, wedmsStatus, isPolling } = useTemplatePdfViewModel(template);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description,
        is_active: template.is_active,
        is_shared: template.is_shared,
      });
      setFields(template.field_mapping?.fields || []);
    }
  }, [template]);

  const handleSave = async () => {
    try {
      setError(null);
      await updateTemplate({
        id: templateId,
        data: {
          ...formData,
          field_mapping: { fields },
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      refetch();
    } catch (err) {
      setError('저장에 실패했습니다.');
    }
  };

  const handleSaveFromPdfEditor = async (updatedFields: FieldMappingItem[]) => {
    setFields(updatedFields);
    try {
      setError(null);
      await updateTemplate({
        id: templateId,
        data: {
          field_mapping: { fields: updatedFields },
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      refetch();
    } catch (err) {
      setError('필드 매핑 저장에 실패했습니다.');
    }
  };

  const handleSaveFromImageEditor = async (fieldMapping: FieldMapping) => {
    setFields(fieldMapping.fields);
    try {
      setError(null);
      await templateService.updateFieldMapping(templateId, fieldMapping);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      refetch();
    } catch (err) {
      setError('필드 매핑 저장에 실패했습니다.');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setError(null);
    try {
      await templateService.uploadImage(templateId, file);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      refetch();
    } catch (err) {
      setError('이미지 업로드에 실패했습니다.');
    } finally {
      setUploadingImage(false);
    }
  };

  if (!template) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? '템플릿 편집' : '템플릿 상세'}
        breadcrumbs={[
          { label: '템플릿', path: '/templates' },
          { label: template.name },
        ]}
        action={
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/templates')}
          >
            목록으로
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          저장되었습니다.
        </Alert>
      )}

      {/* 탭 네비게이션 */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="기본 정보" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" />
                필드 편집
                {template.wedms_doc_id ? (
                  <Chip label="PDF" size="small" color="success" sx={{ height: 20 }} />
                ) : template.has_image ? (
                  <Chip label="이미지" size="small" color="primary" sx={{ height: 20 }} />
                ) : null}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* 탭 0: 기본 정보 */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* 기본 정보 */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  기본 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="템플릿명"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={!isEdit}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="설명"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      multiline
                      rows={2}
                      disabled={!isEdit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          disabled={!isEdit}
                        />
                      }
                      label="활성화"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_shared}
                          onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })}
                          disabled={!isEdit}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {formData.is_shared ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                          {formData.is_shared ? '공유 (모든 사용자)' : '비공개 (본인만)'}
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 필드 매핑 요약 (읽기 전용) */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">필드 매핑</Typography>
                  {isEdit && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setTabValue(1)}
                    >
                      필드 편집
                    </Button>
                  )}
                </Box>

                {isEdit && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    HWPX 문서 내 {'<<__필드명__>>'} 형식의 텍스트와 매핑됩니다.
                    <br />
                    <strong>필드 편집 탭</strong>에서 필드 추가/수정/삭제 및 위치 지정이 가능합니다.
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>필드 ID</TableCell>
                        <TableCell>HWPX 필드</TableCell>
                        <TableCell>라벨</TableCell>
                        <TableCell>타입</TableCell>
                        <TableCell>필수</TableCell>
                        <TableCell>위치</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              {isEdit
                                ? '정의된 필드가 없습니다. 필드 편집 탭에서 필드를 추가하세요.'
                                : '정의된 필드가 없습니다.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        fields.map((field) => (
                          <TableRow key={field.field_id}>
                            <TableCell>{field.field_id}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {field.hwpx_field || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{field.label}</TableCell>
                            <TableCell>
                              {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                            </TableCell>
                            <TableCell>{field.required ? 'Y' : 'N'}</TableCell>
                            <TableCell>
                              {field.position ? (
                                <Chip
                                  label={`${field.position.top.toFixed(0)}%, ${field.position.left.toFixed(0)}%`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="미지정" size="small" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {isEdit && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={isUpdating}
                >
                  {isUpdating ? '저장 중...' : '저장'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/templates')}>
                  취소
                </Button>
              </Box>
            )}
          </Grid>

          {/* 사이드바: 파일 정보 */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  파일 정보
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      파일명
                    </Typography>
                    <Typography variant="body2">{template.preview_filename || template.filename || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      파일 크기
                    </Typography>
                    <Typography variant="body2">
                      {((template.preview_file_size || template.file_size || 0) / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      WeDMS 연동
                    </Typography>
                    <Typography variant="body2" component="div">
                      {wedmsStatus === 'completed' || template.wedms_doc_id ? (
                        <Chip label={`연동됨 (${template.wedms_doc_id})`} size="small" color="success" />
                      ) : wedmsStatus === 'processing' || wedmsStatus === 'pending' ? (
                        <Chip
                          label={isPolling ? '처리 중...' : '대기 중'}
                          size="small"
                          color="info"
                          icon={<CircularProgress size={12} sx={{ ml: 0.5 }} />}
                        />
                      ) : wedmsStatus === 'failed' ? (
                        <Chip label="연동 실패" size="small" color="error" />
                      ) : (
                        <Chip label="미연동" size="small" variant="outlined" />
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      등록자
                    </Typography>
                    <Typography variant="body2">{template.created_by_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      등록일
                    </Typography>
                    <Typography variant="body2">
                      {new Date(template.created_at).toLocaleString('ko-KR')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      수정일
                    </Typography>
                    <Typography variant="body2">
                      {new Date(template.updated_at).toLocaleString('ko-KR')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 탭 1: 필드 편집 (PDF 우선, 이미지 폴백) */}
      <TabPanel value={tabValue} index={1}>
        {/* WeDMS 처리 중일 때 안내 */}
        {(wedmsStatus === 'processing' || wedmsStatus === 'pending') && isPolling ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              WeDMS 문서 처리 중...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              HWPX 파일이 PDF로 변환되고 있습니다. 잠시만 기다려주세요.
              <br />
              완료되면 자동으로 필드 편집이 가능해집니다.
            </Typography>
          </Box>
        ) : hasPdf && pdfUrl ? (
          /* WeDMS 연동 PDF가 있으면 PDF 에디터 사용 */
          loadingPdf ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>PDF 로딩 중...</Typography>
            </Box>
          ) : (
            <PDFFieldMappingEditor
              pdfUrl={pdfUrl}
              initialFields={fields}
              onSave={handleSaveFromPdfEditor}
            />
          )
        ) : pdfError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {pdfError}
          </Alert>
        ) : template.has_image && template.image_url ? (
          /* 이미지가 있으면 이미지 에디터 사용 (폴백) */
          <ImageTemplateEditor
            imageUrl={template.image_url}
            initialFieldMapping={{
              fields: fields,
              exclude_areas: template.field_mapping?.exclude_areas,
            }}
            onSave={handleSaveFromImageEditor}
          />
        ) : (
          /* 둘 다 없으면 이미지 업로드 안내 */
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <UploadIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              필드 편집을 위한 배경이 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              HWPX 파일을 WeDMS에 업로드하면 PDF로 변환되어 필드 위치를 편집할 수 있습니다.
              <br />
              또는 이미지를 업로드하여 필드 위치를 지정할 수 있습니다.
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              id="template-image-upload"
            />
            <label htmlFor="template-image-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
                disabled={uploadingImage}
              >
                {uploadingImage ? '업로드 중...' : '이미지 업로드'}
              </Button>
            </label>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              지원 형식: JPG, PNG, GIF, BMP, WebP
            </Typography>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default TemplateDetailPage;
